import { ReactNode, JSX, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography } from '@altermaind/web/ui';
import { getModeTheme } from '@/_shared/styles/state/selectors';
import { StickyTableSkeleton } from './StickyTableSkeleton';
import { StickyTableWrapper, StyledStickyTable } from './styles';

type Props<T> = {
  columns: {
    label: string;
    key: string;
    renderValue: (item: T, index: number) => string | number | ReactNode;
  }[];
  data: T[];
};

// eslint-disable-next-line @altermaind/react-component-type
const StickyTable = <T,>({ columns, data }: Props<T>): JSX.Element => {
  const themeMode = useSelector(getModeTheme());
  const tableRef = useRef<HTMLTableElement>(null);

  const adjustDivHeights = () => {
    if (tableRef.current) {
      const rows = tableRef.current.querySelectorAll('tr');
      rows.forEach(row => {
        const firstTd = row.querySelector('td:first-child > div');
        if (firstTd) {
          const refHeight = firstTd.clientHeight;
          const tds = row.querySelectorAll('td:not(:first-child)');
          tds.forEach(td => {
            const innerDiv = td.querySelector('div');
            if (innerDiv) {
              innerDiv.style.height = `${refHeight}px`;
            }
          });
        }
      });
    }
  };

  useEffect(() => {
    adjustDivHeights();
    window.addEventListener('resize', adjustDivHeights);
    return () => {
      window.removeEventListener('resize', adjustDivHeights);
    };
  }, []);

  useEffect(() => {
    adjustDivHeights();
  }, [tableRef.current?.id]);

  return (
    <StickyTableWrapper themeMode={themeMode || 'dark'}>
      <StyledStickyTable ref={tableRef} id={`${Math.random()}`} data-testid='sticky-table'>
        <thead>
          <tr>
            {columns.map((column, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <th key={`${column.key}_${index}`}>
                <Typography
                  variant='p2/light'
                  fontSize='xxs'
                  color='grey/light'
                  overflow='hidden'
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {column.label}
                </Typography>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={`row_${idx}`}>
              {columns.map((column, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <td key={`row_${idx}_col_${index}`}>
                  <Box style={!index ? { border: 'none', textAlign: 'left', padding: 0 } : {}}>
                    {column.renderValue(row, index)}
                  </Box>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </StyledStickyTable>
    </StickyTableWrapper>
  );
};

StickyTable.Skeleton = StickyTableSkeleton;

export { StickyTable };
